import React, { createContext, useState, useEffect } from "react";
import { auth } from "../database/firebaseConfig";
import { signOut } from "firebase/auth";
import { authService } from "../Services/authService";
import { signalRService } from "../Services/signalRService";
import { MANUTENCAO_ATIVA, MANUTENCAO_MENSAGEM } from "../config/manutencao";

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [userData, setUserData] = useState(null);
  const [platformData, setPlatformData] = useState(null);
  const [contracts, setContracts] = useState(null);
  const [extracts, setExtracts] = useState(null);
  const [withdraws, setWithdraws] = useState(null);
  const [error, setError] = useState(null);
  const [username, setUsername] = useState(
    sessionStorage.getItem("username-golden")
  );
  const [token, setToken] = useState(sessionStorage.getItem("accessToken"));

  const [sessionId, setSessionId] = useState(
    sessionStorage.getItem("ai_session_id")
  );

  const [goldenVariables, setGoldenVariables] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loadingMessage, setLoadingMessage] = useState("Iniciando conexão...");

  const generateAiSession = () => {
    const sid = `sid_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem("ai_session_id", sid);
    setSessionId(sid);
    return sid;
  };

  console.log(userData)
  useEffect(() => {
    if (!token) return;

    const MAX_INACTIVE_TIME = 20 * 60 * 1000;

    const updateActivity = () => {
      localStorage.setItem("lastActiveTime", Date.now().toString());
    };

    const checkInactivity = () => {
      const lastActive = localStorage.getItem("lastActiveTime");

      if (!lastActive) {
        updateActivity();
        return;
      }

      const now = Date.now();
      const timeDiff = now - parseInt(lastActive, 10);

      if (timeDiff > MAX_INACTIVE_TIME) {
        removeActivityListeners();
        alert(
          "Sessão expirada por inatividade (20min). Por favor, faça login novamente."
        );
        handleLogout(true);
      }
    };

    const addActivityListeners = () => {
      window.addEventListener("mousemove", updateActivity);
      window.addEventListener("keydown", updateActivity);
      window.addEventListener("click", updateActivity);
      window.addEventListener("scroll", updateActivity);
      window.addEventListener("touchstart", updateActivity);
    };

    const removeActivityListeners = () => {
      window.removeEventListener("mousemove", updateActivity);
      window.removeEventListener("keydown", updateActivity);
      window.removeEventListener("click", updateActivity);
      window.removeEventListener("scroll", updateActivity);
      window.removeEventListener("touchstart", updateActivity);
    };

    updateActivity();
    addActivityListeners();

    const intervalId = setInterval(checkInactivity, 60000);

    return () => {
      clearInterval(intervalId);
      removeActivityListeners();
    };
  }, [token]);

  // ==============================================================================

  useEffect(() => {
    const restoreSession = async () => {
      // Manutenção: não restaura sessão (quem estava logado cai no login).
      if (MANUTENCAO_ATIVA) {
        setIsLoading(false);
        return;
      }
      const storedToken = sessionStorage.getItem("accessToken");
      const storedUsername = sessionStorage.getItem("username-golden");
      const storedSid = sessionStorage.getItem("ai_session_id");

      if (storedToken && storedUsername) {
        setToken(storedToken);
        setUsername(storedUsername);

        // 🔑 ADIÇÃO: Garante que haja um Session ID ao restaurar
        if (!storedSid) generateAiSession();

        try {
          await getClientData(storedUsername);
        } catch (err) {
          handleLogout(false);
        }
      } else {
        setIsLoading(false);
      }
    };
    restoreSession();
  }, []);

  const getClientIp = async () => {
    let publicIp = "";
    try {
      const response = await fetch("https://api.ipify.org?format=json");
      if (!response.ok) {
        console.error("Falha ao obter o IP público do usuário.");
      } else {
        const data = await response.json();
        publicIp = data.ip;
      }
      return publicIp;
    } catch (ipError) {
      console.error("Erro ao tentar buscar o IP público:", ipError);
    }
  };

  const login = async (username, password) => {
    // Manutenção: nenhum login chega à API.
    if (MANUTENCAO_ATIVA) throw new Error(MANUTENCAO_MENSAGEM);
    setIsLoading(true);
    setIsDataLoaded(false);
    setError(null);
    try {
      const clientIp = await getClientIp();
      const data = await authService.login(username, password, clientIp);

      if (data.accessToken && data.refreshToken) {
        sessionStorage.setItem("accessToken", data.accessToken);
        localStorage.setItem("refreshToken", data.refreshToken);
        sessionStorage.setItem("username-golden", username);
        localStorage.setItem("lastActiveTime", Date.now().toString());

        // 🔑 ADIÇÃO: Gera sessão da IA no login
        generateAiSession();

        setToken(data.accessToken);
        setUsername(username);

        await getClientData(username);
      } else {
        throw new Error("Resposta de login inválida.");
      }
    } catch (error) {
      const status = error.response?.status;
      const serverMessage = error.response?.data?.message;
      // Rota inexistente (ex.: login de admin desativado) ou usuário não encontrado
      // são exibidos como credenciais inválidas.
      const isInvalidCredentials =
        status === 404 ||
        status === 405 ||
        !serverMessage ||
        serverMessage.includes("Ocorreu um erro ao logar");
      const errorMessage = isInvalidCredentials
        ? "Usuário ou senha inválidos."
        : serverMessage;
      setError(errorMessage);
      handleLogout(false);
      setIsLoading(false);
      throw error;
    }
  };

  const loginWithToken = async (accessToken, refreshToken) => {
    // Manutenção: link de acesso (token-login) também não entra.
    if (MANUTENCAO_ATIVA) throw new Error(MANUTENCAO_MENSAGEM);
    setIsLoading(true);
    setIsDataLoaded(false);
    sessionStorage.setItem("accessToken", accessToken);
    localStorage.setItem("refreshToken", refreshToken);
    sessionStorage.removeItem("username-golden");
    localStorage.setItem("lastActiveTime", Date.now().toString());

    // 🔑 ADIÇÃO: Gera sessão da IA no login via token
    generateAiSession();

    setToken(accessToken);
    setUsername(null);

    await getClientData(null);
  };

  const getClientData = async (usernameForRequest) => {
    let connectionHandler = null;
    const onProgress = (progress, message) => {
      setLoadingProgress(progress);
      setLoadingMessage(message);
    };
    const onError = (errorMessage) => {
      setError(errorMessage);
      setIsLoading(false);
    };

    try {
      connectionHandler = await signalRService.establishConnection(
        onProgress,
        onError
      );
      if (!connectionHandler || !connectionHandler.connectionId) {
        throw new Error(
          "Não foi possível estabelecer uma conexão em tempo real."
        );
      }

      const data = await authService.getClientData(
        usernameForRequest,
        connectionHandler.connectionId
      );

      if (!usernameForRequest && data.client?.username) {
        sessionStorage.setItem("username-golden", data.client.username);
        setUsername(data.client.username);
      }
      setUserData(data.client);
      setPlatformData(data.platformInfo);
      setContracts(data.contracts);
      setWithdraws(data.withdraws);
      setExtracts(data.extracts);
      setGoldenVariables(data.platformInfo?.goldenVariables);
      setError(null);
      setIsDataLoaded(true);

      setTimeout(() => {
        setIsLoading(false);
      }, 1200);
    } catch (error) {
      console.error("Erro ao obter dados do cliente:", error);
      const errorMessage =
        error.message || "Falha ao carregar os dados do usuário.";
      setError(errorMessage);
      setIsLoading(false);
      if (error.response?.status === 401) {
        handleLogout(false);
      }
      throw error;
    } finally {
      if (connectionHandler) {
        signalRService.stopConnection(connectionHandler.connection);
      }
    }
  };

  const handleLogout = (performSignOut = true) => {
    if (userData?.id) {
      sessionStorage.removeItem(`golden_ai_chat_history_${userData.id}`);
    }
    if (performSignOut && auth.currentUser) signOut(auth);

    setUserData(null);
    setPlatformData(null);
    setContracts(null);
    setWithdraws(null);
    setExtracts(null);
    setGoldenVariables(null);
    setToken(null);
    setUsername(null);

    // 🔑 ADIÇÃO: Limpa sessão da IA no logout
    setSessionId(null);
    sessionStorage.removeItem("ai_session_id");

    sessionStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    sessionStorage.removeItem("username-golden");
    localStorage.removeItem("lastActiveTime");
  };

  const reloadUserData = async () => {
    const currentUsername = sessionStorage.getItem("username-golden");
    if (token && currentUsername) {
      setIsLoading(true);
      setIsDataLoaded(false);
      try {
        await getClientData(currentUsername);
      } catch (error) {
        console.error("Erro ao recarregar dados:", error);
        setError("Não foi possível atualizar seus dados.");
      }
    }
  };

  return (
    <AuthContext.Provider
      value={{
        userData,
        platformData,
        contracts,
        withdraws,
        error,
        token,
        username,
        sessionId, // 🔑 Exportado para o chat
        generateAiSession, // 🔑 Exportado para o chat
        isLoading,
        isDataLoaded,
        loadingProgress,
        loadingMessage,
        login,
        logout: () => handleLogout(true),
        reloadUserData,
        loginWithToken,
        goldenVariables,
        isAuthenticated: !!token,
        extracts,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
